import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import {
  PaConfirmModal,
  PaDataTable,
  PaEmptyState,
  PaFilterBar,
  PaPageHeader,
  PaPagination,
} from "components/platform-admin";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import PaAccessDenied from "pages/PlatformAdmin/layout/PaAccessDenied";
import { platformRoleAccess } from "helpers/RolesPermission";
import { getPaginationObj } from "Utils/utils";
import { usePlatformAdmin } from "context/PlatformAdminContext";
import { readGhostSession } from "context/platformAdminContext.utils";
import { URL_PLATFORM_ADMIN_GHOST_SESSIONS } from "store/services/URL";
import {
  fetchPlatformGhostSessions,
  forceEndPlatformGhostSession,
} from "store/services/platformAdmin.services";
import { formatGhostElapsed } from "pages/PlatformAdmin/Ghost/ghostSession.utils";
import {
  buildGhostSessionsQuery,
  DEFAULT_GHOST_SESSION_FILTERS,
  formatGhostParticipant,
  formatGhostSessionElapsed,
  formatGhostTenantLabel,
} from "./ghostSessions.utils";

const ELAPSED_REFRESH_MS = 30_000;

const formatTimestamp = (value) => {
  if (!value) {
    return "—";
  }

  const timestamp = moment(value);
  if (!timestamp.isValid()) {
    return "—";
  }

  return (
    <time dateTime={value} title={timestamp.format("D MMM YYYY, h:mm a")}>
      {timestamp.format("D MMM YYYY, h:mm a")}
    </time>
  );
};

const GhostSessionsList = () => {
  const canViewSessions = platformRoleAccess("platform.ghost.end");
  const { endGhost, ghostSession } = usePlatformAdmin();

  const [filters, setFilters] = useState(DEFAULT_GHOST_SESSION_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(getPaginationObj());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [endingSessionId, setEndingSessionId] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [elapsedTick, setElapsedTick] = useState(0);
  const [confirmSession, setConfirmSession] = useState(null);

  const loadSessions = useCallback(async () => {
    if (!canViewSessions) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = buildGhostSessionsQuery(filters, URL_PLATFORM_ADMIN_GHOST_SESSIONS);
      const payload = await fetchPlatformGhostSessions(endpoint);
      const sessions = payload?.sessions;
      setRows(Array.isArray(sessions?.data) ? sessions.data : []);
      setPagination(getPaginationObj(sessions, filters));
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to load ghost sessions");
    } finally {
      setLoading(false);
    }
  }, [canViewSessions, filters]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) {
          return prev;
        }

        return { ...prev, search: searchInput, page: 1 };
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions, refreshToken]);

  useEffect(() => {
    if (!canViewSessions || rows.length === 0) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setElapsedTick((value) => value + 1);
    }, ELAPSED_REFRESH_MS);

    return () => clearInterval(intervalId);
  }, [canViewSessions, rows.length]);

  const handleForceEnd = useCallback(async (session) => {
    if (!session?.id) {
      return;
    }

    setEndingSessionId(session.id);

    try {
      await forceEndPlatformGhostSession(session.id);

      const activeGhost = readGhostSession();
      if (activeGhost?.jti === session.id) {
        await endGhost({ silent: true, skipNavigate: true });
      }

      toast.success("Ghost session ended");
      setConfirmSession(null);
      setRefreshToken((value) => value + 1);
    } catch (err) {
      toast.error(err?.message || "Failed to force end ghost session");
    } finally {
      setEndingSessionId(null);
    }
  }, [endGhost]);

  const columns = useMemo(
    () => [
      {
        key: "impersonator",
        header: "Impersonator",
        render: (row) => (
          <div className="text-sm text-gray-900">
            {formatGhostParticipant(row.impersonator)}
          </div>
        ),
      },
      {
        key: "acting_user",
        header: "Acting user",
        render: (row) => (
          <div className="text-sm text-gray-900">
            {formatGhostParticipant(row.acting_user)}
          </div>
        ),
      },
      {
        key: "tenant",
        header: "Tenant",
        render: (row) =>
          row.tenant?.sp_id ? (
            <Link
              to={`/tenants/${row.tenant.sp_id}`}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
            >
              {formatGhostTenantLabel(row.tenant)}
            </Link>
          ) : (
            formatGhostTenantLabel(row.tenant)
          ),
      },
      {
        key: "reason",
        header: "Reason",
        render: (row) => (
          <div className="max-w-xs truncate text-sm text-gray-700" title={row.reason || ""}>
            {row.reason || "—"}
          </div>
        ),
      },
      {
        key: "started_at",
        header: "Started",
        render: (row) => formatTimestamp(row.started_at),
      },
      {
        key: "elapsed_seconds",
        header: "Elapsed",
        render: (row) => {
          const elapsed = row.started_at
            ? formatGhostElapsed(row.started_at)
            : formatGhostSessionElapsed(row.elapsed_seconds, row.started_at);

          return (
            <span className="font-mono text-sm text-gray-800" aria-live="polite">
              {elapsed}
            </span>
          );
        },
      },
      {
        key: "actions",
        header: "Actions",
        headerClassName: "text-right",
        cellClassName: "text-right",
        render: (row) => (
          <button
            type="button"
            disabled={endingSessionId === row.id}
            onClick={() => setConfirmSession(row)}
            className="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {endingSessionId === row.id ? "Ending…" : "Force end"}
          </button>
        ),
      },
    ],
    [elapsedTick, endingSessionId]
  );

  if (!canViewSessions) {
    return (
      <PaAccessDenied
        title="Ghost sessions"
        subtitle="Active impersonation sessions across tenants"
        description="You need platform.ghost.end to view ghost sessions."
      />
    );
  }

  return (
    <PlatformAdminLayout>
      <PaPageHeader
        title="Ghost sessions"
        subtitle="Active impersonation sessions across tenants"
      />

      <PaFilterBar
        className="mb-4"
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search impersonator, user, tenant, or reason"
        onClear={
          searchInput
            ? () => {
                setSearchInput("");
                setFilters(DEFAULT_GHOST_SESSION_FILTERS);
              }
            : undefined
        }
        resultCount={pagination.total}
      />

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <PaDataTable
        columns={columns}
        rows={rows}
        rowKey="id"
        isLoading={loading}
        emptyState={
          <PaEmptyState
            title="No active ghost sessions"
            message="No active ghost sessions."
            description={
              ghostSession?.jti
                ? "Your current ghost session may not appear if it was started in another browser."
                : "Active impersonation sessions will appear here."
            }
          />
        }
      />

      <PaPagination
        page={filters.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        rows={rows}
        perPage={filters.perPage}
        loading={loading}
        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
      />

      <PaConfirmModal
        open={Boolean(confirmSession)}
        onClose={() => setConfirmSession(null)}
        title="Force end ghost session?"
        description="The impersonator will lose access immediately. This action is audited."
        confirmLabel="Force end"
        destructive
        submitting={Boolean(endingSessionId)}
        onConfirm={() => handleForceEnd(confirmSession)}
      />
    </PlatformAdminLayout>
  );
};

export default GhostSessionsList;
